/**
 * API service for TalkFlow widget.
 *
 * This service handles all API communication with the TalkFlow backend.
 */

import { getApiBaseUrl, getApiUrl } from '../config/api.js';

/**
 * Send a chat message to the API.
 *
 * @param {string} message - User message
 * @param {string} tenantId - Tenant ID
 * @param {Array} history - Chat history
 * @param {string} apiUrl - API base URL (optional, uses config if not provided)
 * @param {string} sessionId - Existing session ID (optional, creates new if not provided)
 * @returns {Promise<Object>} API response
 */
export const sendMessage = async (message, tenantId, history = [], apiUrl = null, sessionId = null) => {
    // Use configured API URL if not provided
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        // Get or create session
        let currentSessionId = sessionId;

        if (!currentSessionId) {
            // Create new session using the actual endpoint
            const sessionResponse = await fetch(`${baseUrl}/api/v1/chat/sessions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    user_id: tenantId || 'default',
                    title: 'TalkFlow Chat Session',
                    language_preference: 'vi'
                }),
            });

            if (!sessionResponse.ok) {
                const errorText = await sessionResponse.text();
                throw new Error(`Session creation failed: ${sessionResponse.status} - ${errorText}`);
            }

            const sessionData = await sessionResponse.json();
            currentSessionId = sessionData.data?.session_id || sessionData.session_id;

            if (!currentSessionId) {
                console.error('Session response:', sessionData);
                throw new Error('No session ID returned from server');
            }
        }

        // Send message using the correct messages endpoint  
        const response = await fetch(`${baseUrl}/api/v1/chat/messages`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                session_id: currentSessionId,
                message: message,
                user_id: tenantId || 'default',
                enable_rag: true,
                max_tokens: 200,
                temperature: 0.7,
                top_p: 0.9,
                confidence_threshold: 0.7,
                max_documents: 5,
                user_preferences: {
                    language: 'vi'
                },
                context_filters: {}
            }),
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API error: ${response.status} - ${errorText}`);
        }

        const data = await response.json();

        // Transform response to expected format
        return {
            response: data.data?.response || data.response || data.content || data.message,
            tenant_id: tenantId,
            session_id: currentSessionId,
            sources: data.data?.sources_used || data.sources || null,
            suggestions: data.data?.suggestions || data.suggestions || [],
            confidence: data.data?.confidence_score || data.confidence,
            separateReferences: data.data?.separateReferences || data.separateReferences || '',
            separateAttachments: data.data?.separateAttachments || data.separateAttachments || '',
            hasContext: data.data?.hasContext || data.hasContext,
            method: data.data?.method || data.method,
            // Add debug info from API
            model_info: {
                model: data.data?.metadata?.model || data.model,
                strategy_used: data.data?.strategy_used,
                processing_time: data.data?.processing_time,
                conversation_state: data.data?.conversation_state,
                tokens_generated: data.data?.tokens_generated,
                generation_time: data.data?.generation_time,
                tokens_per_second: data.data?.tokens_per_second,
                timestamp: data.meta?.timestamp || data.timestamp
            }
        };

    } catch (error) {
        console.error('Error sending message:', error);
        throw error;
    }
};

/**
 * Get chat sessions for a tenant
 */
export const getChatSessions = async (tenantId, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const response = await fetch(`${baseUrl}/api/v1/chat/sessions?user_id=${encodeURIComponent(tenantId || 'default')}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting chat sessions:', error);
        return [];
    }
};

/**
 * Get a specific chat session
 */
export const getChatSession = async (sessionId, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const response = await fetch(`${baseUrl}/api/v1/chat/sessions/${sessionId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting chat session:', error);
        return null;
    }
};

/**
 * Delete a chat session
 */
export const deleteChatSession = async (sessionId, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const response = await fetch(`${baseUrl}/api/v1/chat/sessions/${sessionId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        return response.ok;
    } catch (error) {
        console.error('Error deleting chat session:', error);
        return false;
    }
};

/**
 * Stream message (for real-time chat)
 */
export const streamMessage = async (sessionId, message, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const response = await fetch(`${baseUrl}/api/v1/chat/sessions/${sessionId}/stream`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                message: message,
                enable_rag: true,
                max_tokens: 200,
                temperature: 0.7,
                top_p: 0.9,
                confidence_threshold: 0.7,
                max_documents: 5
            }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        return response; // Return response for streaming
    } catch (error) {
        console.error('Error streaming message:', error);
        throw error;
    }
};

/**
 * Submit feedback for a message
 */
export const submitFeedback = async (sessionId, messageId, feedback, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const response = await fetch(`${baseUrl}/api/v1/chat/sessions/${sessionId}/feedback`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                message_id: messageId,
                feedback: feedback
            }),
        });

        return response.ok;
    } catch (error) {
        console.error('Error submitting feedback:', error);
        return false;
    }
};

/**
 * Get conversation summary
 */
export const getConversationSummary = async (sessionId, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const response = await fetch(`${baseUrl}/api/v1/chat/sessions/${sessionId}/summary`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting conversation summary:', error);
        return null;
    }
};

// Document Management APIs
/**
 * Upload document
 */
export const uploadDocument = async (file, tenantId, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const formData = new FormData();
        formData.append('document', file);
        formData.append('uploadedBy', tenantId || 'default');
        formData.append('category', 'mental-health');
        formData.append('description', `Document uploaded by ${tenantId || 'default'}`);

        const response = await fetch(`${baseUrl}/api/v1/documents/upload`, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
    }
};

/**
 * Search documents
 */
export const searchDocuments = async (query, tenantId, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const url = new URL(`${baseUrl}/api/v1/documents/search`);
        url.searchParams.append('query', query);
        url.searchParams.append('limit', '10');
        if (tenantId) {
            url.searchParams.append('user_id', tenantId);
        }

        const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error searching documents:', error);
        return [];
    }
};

// Temporary stub functions for backward compatibility
export const getSuggestions = async (query, tenantId, count = 3, apiUrl = null) => {
    // Try to get suggestions from document search
    try {
        const searchResults = await searchDocuments(query, tenantId, apiUrl);
        return searchResults.slice(0, count).map(doc => doc.title || doc.filename);
    } catch (error) {
        console.log('getSuggestions fallback - returning empty array');
        return [];
    }
};

export const getTenantConfig = async (tenantId, apiUrl = null) => {
    return {
        tenant_id: tenantId || 'default',
        name: 'Default Tenant',
        settings: {}
    };
};

/**
 * Upload file and get RAG suggestions
 */
export const uploadFileWithSuggestions = async (file, tenantId, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        // First upload the file
        const uploadResult = await uploadDocument(file, tenantId, apiUrl);

        if (!uploadResult.success || !uploadResult.data?.id) {
            throw new Error('File upload failed');
        }

        // Then get suggestions based on the uploaded document
        const suggestionsResponse = await fetch(`${baseUrl}/api/v1/documents/suggestions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                document_id: uploadResult.data.id,
                tenant_id: tenantId || 'default',
                count: 5
            }),
        });

        let suggestions = [];
        if (suggestionsResponse.ok) {
            const suggestionsResult = await suggestionsResponse.json();
            suggestions = suggestionsResult.data?.suggestions || [];
        } else {
            console.warn('Failed to get suggestions, proceeding without them');
        }

        return {
            upload: uploadResult.data,
            suggestions: suggestions,
            success: true
        };

    } catch (error) {
        console.error('Error uploading file with suggestions:', error);
        throw error;
    }
};

/**
 * Get document-based question suggestions
 */
export const getDocumentQuestions = async (content, tenantId, count = 3, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const response = await fetch(`${baseUrl}/api/v1/documents/questions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                content: content,
                tenant_id: tenantId || 'default',
                count: count
            }),
        });

        if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
        }

        const result = await response.json();
        return result.data?.questions || [];

    } catch (error) {
        console.error('Error getting document questions:', error);
        return [];
    }
};

/**
 * Create a new chat session
 */
export const createSession = async (tenantId, apiUrl = null) => {
    const baseUrl = apiUrl || getApiBaseUrl();
    try {
        const response = await fetch(`${baseUrl}/api/v1/chat/sessions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                clientId: tenantId || 'default',
                domain: window.location.hostname || 'localhost',
                userAgent: navigator.userAgent
            }),
        });

        if (!response.ok) {
            const errorData = await response.text();
            throw new Error(`API error: ${response.status} - ${errorData}`);
        }

        const data = await response.json();
        return data.data?.sessionId || data.data?.session_id || data.sessionId || data.session_id;

    } catch (error) {
        console.error('Error creating session:', error);
        throw error;
    }
};
